import { useEffect, useState } from 'react';

import SocialButton from 'src/components/consumptionPage/ConsumptionMetaData/SocialButtons/SocialButton';
import {
  CardDetailsType,
  ReferralContentItemType
} from 'src/components/thumbnail/types';
import ToastMessage from 'src/components/toastMessage';
import { useAppStore } from 'src/hooks/useAppStore';
import { mixpanelObject } from 'src/utils/analytics';
import {
  contentPropertySetter,
  ctaEvent,
  shareEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { ContentItemType } from 'src/utils/commonTypes';
import { IconsSetType, NewIconsSet } from 'src/utils/constants/IconsConstants';
import {
  CountryCode,
  ILT_GRID_LIST,
  LINK_COPIED
} from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { getFeatureFlagConfig, isFeatureEnabled } from 'src/utils/RemoteConfig';

import ShareModal from './ShareModal/ShareModal';
import * as customStyled from './ShareStyle';
import {
  getReferralShareLink,
  oneLinkApiCall
} from '../../../services/apiRequests/ShareService';
import {
  MIXPANEL_ELEMENT,
  MIXPANEL_TAB_NAME
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { ZEE5_BASE_ADDRESS } from '../../../utils/constants/ApiConstants';

type ShareProps = {
  item?: ContentItemType;
  fromThumbnail?: boolean;
  fromCarousel: boolean;
  isReferral?: boolean;
  onClosePopup?: () => void;
  cssClassName?: string;
  cardDetails?: CardDetailsType;
  detailsPageFlag?: boolean;
  isSportsLiveEvent?: boolean;
  isReferralURL?: boolean;
  /**
   * This component is normally used for content sharing, but that will pick up a template from translation.
   * Hence, using this prop in case we need to share some other features too (ex. quiz, etc.)
   */
  overrideShareText?: string;
  /**
   * Normally onelink URL is used here.
   * Hence, using this prop in case we need to override that URL
   */
  overrideShareURL?: string;
};

const Share = (props: ShareProps) => {
  const {
    item,
    fromThumbnail,
    isReferralURL,
    fromCarousel,
    cardDetails,
    isReferral,
    onClosePopup,
    cssClassName,
    detailsPageFlag,
    overrideShareText,
    overrideShareURL
  } = props;

  const {
    translations,
    appConfig,
    isUserLoggedIn,
    remoteConfig,
    usersLocationData
  } = useAppStore();

  const country = usersLocationData?.country_code || CountryCode.IN;
  const translationData = translations?.consumption;
  const NEW_SUBSCRIPTION_STRINGS = translations?.subscription;
  const { SHARE } = translationData || {};
  const isFeatureReferral =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.CONFIG_REFERRAL) ||
    false;

  const [showToastMsg, setShowToastMsg] = useState(false);
  const [oneLinkUrl, setOneLinkUrl] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showShareForMobile, setShowShareForMobile] = useState(false);
  const [referralContentItem, setReferralContentItem] =
    useState<ReferralContentItemType>();

  const shareURL = `${ZEE5_BASE_ADDRESS}${item?.slug}`;
  //Set title for sharing doc
  const title = item?.title || '';

  useEffect(() => {
    if (
      (isReferral || isReferralURL) &&
      !(showSharePopup || showShareForMobile)
    ) {
      if ((appConfig?.deviceWidth as number) >= 1200) {
        if (!showSharePopup) {
          setShowSharePopup(true);
        }
      } else if (!showShareForMobile) {
        setShowShareForMobile(true);
      }
    }
  }, [appConfig?.deviceWidth]);

  const closeSharePopup = () => {
    setShowSharePopup((prevState) => !prevState);
    if ((isReferral || isReferralURL) && onClosePopup) {
      onClosePopup();
    }
  };

  const toastMsgHandler = () => {
    setShowToastMsg(true);
  };

  const shareMixpanelEvent = () => {
    mixpanelObject.setProperty(MIXPANEL_TAB_NAME, 'N/A');
    shareEvent();
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, ILT_GRID_LIST.CONTENT_SHARE);
    ctaEvent();
  };

  const getDeepLinkResponse = async () => {
    try {
      let text = '';
      let isReplaceMessageContent = false;
      let refferalLink = '';
      let oneLinkResponse = '';
      if (!oneLinkUrl && (showSharePopup || showShareForMobile)) {
        if (isUserLoggedIn && isFeatureReferral) {
          refferalLink = await getReferralShareLink();
          if (refferalLink && remoteConfig) {
            const { payload } = getFeatureFlagConfig(
              remoteConfig,
              FeatureFlagConstants.CONFIG_REFERRAL
            );
            const offerPercentage =
              country.toLowerCase() === 'in'
                ? payload['REFERRAL_OFFER']
                : payload['REFERRAL_OFFER_GB'];

            let referralMessage = isReferral
              ? NEW_SUBSCRIPTION_STRINGS?.Referral_Landing_ReferralMessage_Text
              : NEW_SUBSCRIPTION_STRINGS?.Referral_Consumption_Share_Text;
            referralMessage = referralMessage.replace(
              '{{referral_discount}}',
              offerPercentage
            );
            text = `${referralMessage}\n`;
            isReplaceMessageContent = true;
          }
        }
        oneLinkResponse = await oneLinkApiCall(shareURL);
        setOneLinkUrl(oneLinkResponse);
        // Share message - Non referral landing page
        if (isReplaceMessageContent) {
          text = text.replace('{{one_link_url}}', oneLinkResponse || shareURL);
          text = text.replace('{{content_title}}', title);
          setReferralContentItem({
            title: text,
            referralDeepLink: refferalLink
          });

          if (refferalLink) {
            setOneLinkUrl(refferalLink);
          } else {
            const oneLinkResponse = await oneLinkApiCall(shareURL);
            setOneLinkUrl(oneLinkResponse);
          }
        } else {
          const oneLinkResponse = await oneLinkApiCall(shareURL);
          setOneLinkUrl(oneLinkResponse);
        }
      }
      if (showShareForMobile) {
        if (navigator?.share) {
          setTimeout(async () => {
            try {
              await navigator.share({
                title,
                text: text || item?.description,
                url: refferalLink || oneLinkResponse || shareURL
              });
              contentPropertySetter(mixpanelObject, item, cardDetails);
              shareEvent();
            } catch (error) {
              console.error('Error while sharing url in mobile: ', error);
            }
          }, 2000);
        } else {
          setShowShareForMobile(false);
          setShowSharePopup(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getDeepLinkResponse();
  }, [showSharePopup, showShareForMobile]);

  useEffect(() => {
    if (showToastMsg) {
      setTimeout(() => {
        setShowToastMsg(false);
      }, 2000);
    }
    if (showShareForMobile) {
      setTimeout(() => {
        setShowShareForMobile(false);
      }, 2000);
    }
  }, [showToastMsg, showShareForMobile]);

  const shareHandler = () => {
    if ((appConfig?.deviceWidth as number) >= 1200) {
      setShowSharePopup(true);
    } else {
      setShowShareForMobile(true);
    }
    shareMixpanelEvent();
  };

  return (
    <div css={customStyled.shareContainer} data-testid="share-btn-container">
      {!isReferralURL && !isReferral ? (
        fromThumbnail || fromCarousel ? (
          <div
            data-testid="share-btn"
            css={customStyled.shareButton}
            onClick={shareHandler}
            title={translations?.consumption?.SHARE_BUTTON_TEXT}>
            <span css={customStyled.shareIconForThumbnail}></span>
            <span>{translations?.consumption?.SHARE_BUTTON_TEXT}</span>
          </div>
        ) : (
          <SocialButton
            icon={NewIconsSet.SHARE}
            type={IconsSetType.NEW}
            action={shareHandler}
            name={SHARE}
            detailsPageFlag={detailsPageFlag}
            id="share-btn"
          />
        )
      ) : null}

      {showSharePopup && (
        <ShareModal
          isOpen={showSharePopup}
          handleClose={closeSharePopup}
          item={item}
          shareURL={
            overrideShareURL
              ? overrideShareURL
              : referralContentItem?.referralDeepLink || oneLinkUrl || shareURL
          }
          toastMsgHandler={toastMsgHandler}
          cardDetails={cardDetails}
          cssClassName={cssClassName}
          referralContentItem={referralContentItem}
          isFeatureReferral={isFeatureReferral}
          overrideShareText={overrideShareText || ''}
        />
      )}
      {showToastMsg ? <ToastMessage message={LINK_COPIED} /> : null}
    </div>
  );
};

export default Share;
